import React, { useState, useRef, useEffect } from "react";
import { Row } from "react-bootstrap";
import html2canvas from "html2canvas";
import { MyDiv } from "../Common/Components";
import BarcodeSection from "./barcodeSection";
import axios from "axios";
import swal from "sweetalert2";
import PropTypes from "prop-types";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
// unitDbId (optional): when set, the component runs in per-unit mode — it
// fetches and posts the barcode against /api/order-units/:unitId instead of
// the order-level endpoints. Absent → unchanged order-level behavior.
// forceRender (optional): bypasses the order_barcode_checker gate so a
// resend on an already-pushed order/unit still re-renders + re-emails.
// Initial production push leaves this false so the gate still prevents
// accidental double-pushes.
function BarCodeList({ orderId, BarcodeSuccessInfo, unitDbId = null, forceRender = false }) {
  const [barCodeList, setBarcodeList] = useState([]);

  const wrapperRefs = {
    flashing: useRef(),
    jobbing: useRef(),
    faciagutter: useRef(),
    cladding: useRef(),
    gbi: useRef(),
    roofing: useRef(),
    rv: useRef(),
    awf: useRef(),
  };

  let savedOrderId = orderId;
  useEffect(() => {
    if (unitDbId || (savedOrderId?.id && savedOrderId?.department)) {
      loadSpecificBarcodeList();
    }
  }, [orderId?.updatedAt, unitDbId]);

  const loadSpecificBarcodeList = async () => {
    const url = unitDbId
      ? `${API_BASE_URL}api/order-units/${unitDbId}/barcode`
      : API_BASE_URL + `fetch-specific-order-barcode/${orderId.id}`;
    try {
      const response = await fetch(url, {
        headers: {
          "x-access-token": localStorage.getItem("token"),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data[0] && (forceRender || data[0].order_barcode_checker === false)) {
        setBarcodeList(data);
      }
    } catch (error) {
      BarcodeSuccessInfo();
      swal.fire({
        text: error.response?.data || "An error occurred",
        icon: "error",
      });
    }
  };

  // useEffect(() => {
  //   if (Array.isArray(barCodeList) && barCodeList.length !== 0) {
  //     if (orderId || unitDbId) {
  //       handleSaveAndSendToAPI();
  //     }
  //   }
  // }, [barCodeList]);

 useEffect(() => {
    if (!Array.isArray(barCodeList) || barCodeList.length === 0) return;

    if (!(orderId || unitDbId)) return;
    
    handleSaveAndSendToAPI();
  }, [barCodeList]);

  const handleSaveAndSendToAPI = async () => {
    const canvasWidth = 1800;
    const canvasHeight = 1200;
    const opt = {
      scale: 4,
    };

    const formData = new FormData();
    let barcodeRefs;

    // Per-unit mode is always Flashing; order mode reads orderId.department.
    const department = unitDbId ? "F" : orderId.department;

    if (department === "F") {
      barcodeRefs = [wrapperRefs.flashing.current];
    } else if (department === "J") {
      barcodeRefs = [wrapperRefs.jobbing.current];
    } else if (department === "FG") {
      barcodeRefs = [wrapperRefs.faciagutter.current];
    } else if (department === "CL") {
      barcodeRefs = [wrapperRefs.cladding.current];
    } else if (department === "GBI") {
      barcodeRefs = [wrapperRefs.gbi.current];
    } else if (department === "ROOF") {
      barcodeRefs = [wrapperRefs.roofing.current];
    } else if (department === "RV") {
      barcodeRefs = [wrapperRefs.rv.current];
    } else if (orderId.department === "AWF") {
      barcodeRefs = [wrapperRefs.awf.current];
    }

    for (let index = 0; index < barcodeRefs.length; index++) {
      const barcodeRef = barcodeRefs[index];

      try {
        const canvas = await html2canvas(barcodeRef, opt);
        const resizedCanvas = document.createElement("canvas");
        resizedCanvas.width = canvasWidth;
        resizedCanvas.height = canvasHeight;
        const ctx = resizedCanvas.getContext("2d");
        ctx.drawImage(canvas, 0, 0, canvasWidth, canvasHeight);
        const blob = await new Promise(resolve => resizedCanvas.toBlob(resolve, "image/jpeg", 0.9));
        formData.append("documents", blob, `barcode_${index}.jpg`);
      } catch (error) {
        console.log(error);
      }
    }

    // Add AWF notes / subject override / customer-copy keys if present
    if (orderId?.department === "AWF" && orderId.awfNotes) {
      formData.append("awfNotes", orderId.awfNotes);
    }
    if (orderId?.department === "AWF" && orderId.awfSubject) {
      formData.append("awfSubject", orderId.awfSubject);
    }
    if (orderId?.department === "AWF" && orderId.awfCustomerCopyKeys?.length) {
      formData.append("awfCustomerCopyKeys", JSON.stringify(orderId.awfCustomerCopyKeys));
    }

    const apiEndpoint = unitDbId
      ? `${API_BASE_URL}api/order-units/${unitDbId}/sent-barcode-designs`
      : `${API_BASE_URL}sent-barcode-designs-for-printing/${orderId.id}/${orderId.department}`;
    try {
      const response = await axios.post(apiEndpoint, formData, {
        headers: {
          "x-access-token": localStorage.getItem("token"),
          Accept: "application/json",
        },
      });
      // The order still moved to production, but some non-blocking part of the
      // send failed (e.g. the Fascia Gutter machine file email). Show it without
      // auto-dismissing so the user actually sees it and can resend.
      const warnings = response?.data?.warnings;
      if (warnings?.length) {
        swal.fire({
          title: "Processed with warnings",
          text: warnings.join("; "),
          icon: "warning",
          type: "warning",
        });
      } else {
        swal.fire({
          text: "Processed Successfully",
          icon: "success",
          type: "success",
          timer: 1000,
        });
      }
      BarcodeSuccessInfo();
      savedOrderId = "";
    } catch (error) {
      BarcodeSuccessInfo();
      swal.fire({
        text: error.response.data,
        icon: "error",
        type: "error",
      });
    }
  };

  return (
    <React.Fragment>
      {barCodeList.map(item => (
        <MyDiv key={item._id}>
          <Row>
            {(unitDbId || item.Order_Flashing_Count !== 0) && <BarcodeSection item={item} department="Flashing" barcodeRef={wrapperRefs.flashing} />}
            {item.Order_Faciagutter_Count !== 0 && <BarcodeSection item={item} department="Facia Gutter" barcodeRef={wrapperRefs.faciagutter} />}
            {item.Order_Jobbing_Count !== 0 && <BarcodeSection item={item} department="Jobbing" barcodeRef={wrapperRefs.jobbing} />}
            {item.Order_Cladding_Count !== 0 && <BarcodeSection item={item} department="Cladding" barcodeRef={wrapperRefs.cladding} />}
            {item.Order_GBI_Count !== 0 && <BarcodeSection item={item} department="GBI" barcodeRef={wrapperRefs.gbi} />}
            {item.Order_Roofing_Count !== 0 && <BarcodeSection item={item} department="Roofing" barcodeRef={wrapperRefs.roofing} />}
            {item.Order_RV_Count !== 0 && <BarcodeSection item={item} department="RV" barcodeRef={wrapperRefs.rv} />}
          </Row>
        </MyDiv>
      ))}
    </React.Fragment>
  );
}

export default BarCodeList;

BarCodeList.propTypes = {
  orderId: PropTypes.any,
  BarcodeSuccessInfo: PropTypes.any,
};